import React from 'react';
import styled, { css } from 'styled-components';

// Onaylarım paneli ve ana panelin üst istatistik satırı AYNI kartı kullanır;
// tek fark ikon rozetinin dolgusu (panelde marka gradyanı + gölgesiz, ana
// panelde düz palet rengi + renkli `iconShadow`). Tek bileşen olmasının
// sebebi: iki kopya zamanla ayrışır ve iki farklı kart dili doğar.
const Tile = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px;
  background: ${({ theme }) => theme.colors.surface.card};
  border-radius: ${({ theme }) => theme.metrics.radius.large};
  box-shadow: 0 1px 4px rgba(${({ theme }) => theme.colors.shadow.rgb}, 0.06);

  /* Kenarlık durgun hâlde de VAR, yalnız şeffaf. Hover'da yalnız rengi
     değişir — aksi hâlde kart 1px büyüyüp ızgarayı oynatırdı. */
  border: 1px solid transparent;

  ${({ $clickable }) => $clickable && css`
    cursor: pointer;

    /* Hareketi kapatmış kullanıcıda kart ZIPLAMAZ ama kenarlık ve gölge yine
       değişir — geri bildirim kaybolmaz, yalnız yer değiştirme olmaz. */
    @media (prefers-reduced-motion: no-preference) {
      transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;

      &:hover { transform: translateY(-3px); }
    }

    &:hover,
    &:focus-visible {
      border-color: ${({ theme }) => theme.colors.primary.main};
      box-shadow: 0 8px 20px rgba(2, 145, 239, 0.16);
    }

    &:focus-visible { outline: none; }
  `}

  ${({ $muted }) => $muted && css`opacity: 0.62;`}
`;

const Top = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
`;

const IconBadge = styled.div`
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border-radius: 11px;
  background: ${({ $background }) => $background};
  color: ${({ $color }) => $color};
  box-shadow: ${({ $shadow }) => $shadow || 'none'};
`;

const Value = styled.div`
  font-size: 30px;
  font-weight: 700;
  line-height: 1;
  color: ${({ theme, $muted }) => ($muted ? '#9AA7B0' : theme.colors.text.primary)};
`;

const Label = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Sublabel = styled.div`
  margin-top: 3px;
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

/**
 * Paylaşılan istatistik kartı — Onaylar paneli (9 kart, ikon dolgusu marka
 * gradyanı) ve ana panelin üst istatistik satırı (4 kart, ikon dolgusu düz
 * palet rengi) TARAFINDAN ORTAK kullanılır. Bu bileşen yalnızca sunumsaldır:
 * veri erişimi, yönlendirme ya da i18n çevirisi yapmaz — çağıran, bitmiş
 * string'leri hazır geçirir.
 *
 * @param {React.ComponentType} icon - lucide-react ikon bileşeni.
 * @param {string} iconBackground - İkon rozetinin CSS `background` değeri
 *   (gradyan ya da düz renk). Verilmezse marka gradyanına düşer.
 * @param {string} iconColor - İkon rengi.
 * @param {string} [iconShadow] - İkon rozetinin `box-shadow` değeri (ör. ana panelin
 *   düz palet renkleriyle eşleşen renkli gölge). Verilmezse gölge yok ('none').
 * @param {number} value - Gösterilecek sayı (ör. bekleyen talep sayısı).
 * @param {string} label - Kart başlığı.
 * @param {string} [sublabel] - İkincil açıklama metni.
 * @param {Function} [onClick] - Verilmezse kart tıklanabilir DEĞİLDİR: hover
 *   efekti, `role` ve `tabIndex` uygulanmaz (salt bilgi kartı olur).
 * @param {boolean} [muted] - `0` bekleyen kart için soluk hâl (kart soluklaşır,
 *   sayı gri olur).
 */
export default function StatTileCard({
  icon: Icon,
  iconBackground = 'linear-gradient(135deg, #0291EF 0%, #02D3BF 100%)',
  iconColor = '#fff',
  iconShadow = 'none',
  value,
  label,
  sublabel,
  onClick,
  muted = false,
}) {
  const clickable = typeof onClick === 'function';

  // Kart bir <div>; tıklanabilir olduğunda klavye kullanıcısı için düğme
  // semantiği kazanır. Space da Enter gibi tetikler (yerel düğme davranışı).
  const handleKeyDown = (e) => {
    if (!clickable) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(e);
    }
  };

  return (
    <Tile
      $clickable={clickable}
      $muted={muted}
      onClick={clickable ? onClick : undefined}
      onKeyDown={handleKeyDown}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      <Top>
        <IconBadge $background={iconBackground} $color={iconColor} $shadow={iconShadow}>
          {Icon ? <Icon size={21} /> : null}
        </IconBadge>
        <Value $muted={muted}>{value}</Value>
      </Top>
      <div>
        <Label>{label}</Label>
        {sublabel ? <Sublabel>{sublabel}</Sublabel> : null}
      </div>
    </Tile>
  );
}
